import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, Loader2, Mail, RotateCcw, Target, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export function SystemeUImportCard() {
  const [stats, setStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/maps/systeme-u/stats');
      setStats(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const stores = Array.isArray(parsed) ? parsed : (parsed.stores || []);
      const { data: r } = await api.post('/maps/systeme-u/import-json', { stores });
      toast.success(
        `Import Trombinoscope : ${r.inserted} ajoutés, ${r.updated} MAJ, ${r.skipped_non_hyper_super} ignorés (non Hyper/Super).`,
        { duration: 8000 }
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erreur import JSON');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const downloadExtractJson = () => {
    // Pre-extracted JSON of 838 Hyper/Super U stores, served from /public.
    // Useful to pre-populate production via "Importer JSON extrait" afterwards.
    window.open('/downloads/trombinoscope_u_extrait.json', '_blank');
    toast.success('Téléchargement du JSON pré-extrait (838 magasins) lancé…');
  };

  const exportExcel = async (classification = 'all') => {
    setExporting(true);
    try {
      const resp = await api.get(`/maps/systeme-u/export-excel?classification=${classification}`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `magasins_systeme_u_${classification}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur export Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportExcelWithMail = async () => {
    setExporting(true);
    try {
      const resp = await api.get(`/maps/systeme-u/export-excel-with-mail?classification=prospects`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prospection_systeme_u_mails_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel + mails personnalisés téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur export Excel avec mail');
    } finally {
      setExporting(false);
    }
  };

  const createProspects = async () => {
    const count = stats?.classification?.prospects_available || 0;
    if (count === 0) {
      toast.error('Aucune cible disponible. Importez d’abord le JSON.');
      return;
    }
    const nom = window.prompt(
      `Créer ${count} prospects depuis les cibles Système U.\n\nNom de la campagne (laisser vide pour "Système U 2024-2025") :`,
      'Système U 2024-2025 (Trombinoscope)'
    );
    if (nom === null) return;  // cancel
    setCreating(true);
    try {
      const { data: r } = await api.post('/maps/systeme-u/bulk-create-prospects', {
        nouvelle_campagne_nom: nom.trim() || 'Système U 2024-2025 (Trombinoscope)',
      });
      toast.success(
        `${r.created} prospects créés ! ${r.skipped_duplicate} doublons ignorés. Enrichissement SerpAPI lancé pour les ${r.enrichment_queued} premiers.`,
        { duration: 10000 }
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création prospects');
    } finally {
      setCreating(false);
    }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data: r } = await api.post('/maps/systeme-u/rebuild-cross-reference');
      toast.success(`Re-classification terminée : ${r.rebuilt} magasins ré-étiquetés.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur re-classification');
    } finally {
      setRebuilding(false);
    }
  };

  const total = stats?.total || 0;
  const hyper = stats?.by_enseigne?.['HYPER U'] || 0;
  const superU = stats?.by_enseigne?.['SUPER U'] || 0;
  const wasabiCount = stats?.classification?.wasabi_clients || 0;
  const hanaCount = stats?.classification?.hanagroup_excluded || 0;
  const prospectsCount = stats?.classification?.prospects_available || 0;
  const withMgr = stats?.with_manager || 0;

  return (
    <Card className="border-2 border-[#1E40AF]/20 shadow-sm bg-gradient-to-br from-[#EFF6FF] to-white" data-testid="systeme-u-import-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#1E40AF]/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-[#1E40AF]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold">Trombinoscope Système U</h3>
            <p className="text-xs text-[#71717A] mt-0.5">
              Import AI (Gemini) du PDF Trombinoscope associés U. Garde uniquement <strong>Hyper U</strong> et <strong>Super U</strong> (U Express, Utile, Drive ignorés). Croise avec clients Wasabi et positions HANAGROUP.
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-white/70 rounded-lg px-3 py-2 border border-[#1E40AF]/20">
            <div className="text-[#1E40AF] font-semibold text-lg">{hyper}</div>
            <div className="text-[#71717A]">Hyper U importés</div>
          </div>
          <div className="bg-white/70 rounded-lg px-3 py-2 border border-[#DC2626]/20">
            <div className="text-[#DC2626] font-semibold text-lg">{superU}</div>
            <div className="text-[#71717A]">Super U importés</div>
          </div>
          <div className="bg-white/70 rounded-lg px-3 py-2 border border-[#64748B]/20">
            <div className="text-[#334155] font-semibold text-lg">{withMgr}</div>
            <div className="text-[#71717A]">avec dirigeant·e</div>
          </div>
        </div>

        {/* Classification breakdown */}
        <div className="space-y-1.5 bg-white/60 rounded-lg p-3 border border-[#1E40AF]/15">
          <div className="text-[11px] font-semibold text-[#334155] uppercase tracking-wide mb-1">Classification automatique ({total} magasins)</div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-[#047857]">
              <Target className="w-3.5 h-3.5"/>
              Cibles potentielles (prospects)
            </span>
            <strong className="text-[#047857]" data-testid="su-prospects-count">{prospectsCount}</strong>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-[#10B981]">
              <CheckCircle2 className="w-3.5 h-3.5"/>
              Déjà clients Wasabi (exclus)
            </span>
            <strong className="text-[#10B981]" data-testid="su-wasabi-count">{wasabiCount}</strong>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-[#C2410C]">
              <XCircle className="w-3.5 h-3.5"/>
              Bloqués HANAGROUP (interdits)
            </span>
            <strong className="text-[#C2410C]" data-testid="su-hanagroup-count">{hanaCount}</strong>
          </div>
        </div>

        {/* Actions principales */}
        <div className="flex gap-2">
          <label className="flex-1">
            <input type="file" accept=".json" className="hidden" onChange={onUpload} disabled={importing}
              data-testid="systeme-u-upload-input"/>
            <Button variant="outline" className="w-full h-9 text-xs border-[#1E40AF]/40 text-[#1E40AF] hover:bg-[#EFF6FF] cursor-pointer" asChild disabled={importing}
              data-testid="systeme-u-upload-btn">
              <span>
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Upload className="w-3.5 h-3.5 mr-1.5"/>}
                Importer JSON extrait
              </span>
            </Button>
          </label>
          <Button variant="outline" onClick={downloadExtractJson}
            className="h-9 text-xs border-[#1E40AF]/40 text-[#1E40AF] hover:bg-[#EFF6FF]"
            data-testid="systeme-u-download-extract-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5"/>
            Télécharger JSON (838 magasins)
          </Button>
        </div>

        {/* Actions export + bulk create */}
        {total > 0 && (
          <div className="flex flex-col gap-2 pt-1 border-t border-[#1E40AF]/10">
            <div className="text-[11px] font-semibold text-[#334155] uppercase tracking-wide pt-2">
              Export & création prospects
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => exportExcel('prospects')} disabled={exporting}
                className="h-9 text-xs border-[#047857]/40 text-[#047857] hover:bg-[#ECFDF5]"
                data-testid="systeme-u-export-prospects-btn">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Target className="w-3.5 h-3.5 mr-1.5"/>}
                Exporter cibles ({prospectsCount})
              </Button>
              <Button variant="outline" onClick={() => exportExcel('all')} disabled={exporting}
                className="h-9 text-xs border-[#1E40AF]/40 text-[#1E40AF] hover:bg-[#EFF6FF]"
                data-testid="systeme-u-export-all-btn">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <FileText className="w-3.5 h-3.5 mr-1.5"/>}
                Exporter tout ({total})
              </Button>
            </div>
            <Button variant="outline" onClick={() => exportExcelWithMail()} disabled={exporting}
              className="w-full h-9 text-xs border-[#047857]/40 text-[#047857] hover:bg-[#ECFDF5] bg-[#ECFDF5]"
              data-testid="systeme-u-export-mail-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Mail className="w-3.5 h-3.5 mr-1.5"/>}
              Exporter Excel + mails personnalisés ({prospectsCount})
            </Button>
            <Button onClick={createProspects} disabled={creating || prospectsCount === 0}
              className="w-full h-10 text-sm bg-[#047857] hover:bg-[#065F46] text-white font-semibold"
              data-testid="systeme-u-create-prospects-btn">
              {creating ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1.5"/> Création en cours…</>
              ) : (
                <><Target className="w-4 h-4 mr-1.5"/> Créer {prospectsCount} prospects dans le phoning</>
              )}
            </Button>
            <Button variant="ghost" onClick={rebuild} disabled={rebuilding}
              className="h-7 text-xs text-[#64748B] hover:bg-[#F1F5F9]"
              data-testid="systeme-u-rebuild-btn">
              {rebuilding ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <RotateCcw className="w-3 h-3 mr-1"/>}
              Re-classifier (après MAJ base Wasabi/HANAGROUP)
            </Button>
          </div>
        )}

        <div className="bg-[#DBEAFE] border border-[#93C5FD] rounded-lg p-2.5 text-[11px] text-[#1E3A8A]">
          💡 <strong>Workflow</strong> : (1) "Télécharger JSON" pour récupérer les 838 magasins pré-extraits · (2) "Importer JSON extrait" pour les charger en base · (3) "Créer X prospects" pour les ajouter automatiquement à une campagne de phoning (exclut clients Wasabi + HANAGROUP). Enrichissement SerpAPI auto pour retrouver les téléphones.
        </div>
      </CardContent>
    </Card>
  );
}
